
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { IconSearch, IconX, IconLoader } from "@/components/ui/Icons";
import type { SearchSuggestion } from "@/types";

interface SearchBarProps {
  compact?: boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
}

export function SearchBar({
  compact = false,
  className,
  placeholder = "Search Squids...",
  autoFocus = false,
  onSearch,
}: SearchBarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `/api/search/suggestions?q=${encodeURIComponent(q.trim())}`
      );
      if (!res.ok) return;

      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setActiveIndex(-1);
    setIsOpen(true);

    clearTimeout(debounceRef.current);
    if (value.trim().length >= 2) {
      debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    } else {
      setSuggestions([]);
    }
  };

  const handleSubmit = (q?: string) => {
    const searchQuery = (q || query).trim();
    if (!searchQuery) return;

    setIsOpen(false);
    setActiveIndex(-1);

    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "Enter") handleSubmit();
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && suggestions[activeIndex]) {
          handleSubmit(suggestions[activeIndex].text);
        } else {
          handleSubmit();
        }
        break;
      case "Escape":
        setIsOpen(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const suggestionIcons: Record<string, React.ReactNode> = {
    query: <IconSearch className="w-3.5 h-3.5 text-text-subtle" />,
    topic: (
      <svg className="w-3.5 h-3.5 text-text-subtle" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
    user: (
      <svg className="w-3.5 h-3.5 text-text-subtle" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative",
        compact ? "w-full" : "w-full max-w-xl",
        className
      )}
    >
      <div
        className={cn(
          "relative flex items-center rounded-xl border transition-all duration-150",
          "bg-surface",
          isOpen && query
            ? "border-brand/50 ring-2 ring-brand/20"
            : "border-input hover:border-border-strong"
        )}
      >
        <div className="absolute left-3 flex items-center pointer-events-none">
          {loading ? (
            <IconLoader className="w-4 h-4 text-text-subtle" />
          ) : (
            <IconSearch className="w-4 h-4 text-text-subtle" />
          )}
        </div>

        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsOpen(true);
            if (query.length >= 2) fetchSuggestions(query);
          }}
          placeholder={placeholder}
          className={cn(
            "w-full bg-transparent border-0 outline-none text-sm text-foreground",
            "placeholder:text-text-subtle",
            compact ? "h-9 pl-9 pr-9" : "h-11 pl-10 pr-10"
          )}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={isOpen && suggestions.length > 0}
          aria-autocomplete="list"
          aria-controls="search-suggestions"
          aria-activedescendant={
            activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined
          }
        />

        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 p-0.5 rounded-md text-text-subtle hover:text-foreground hover:bg-muted transition-colors"
            tabIndex={-1}
            aria-label="Clear search"
          >
            <IconX className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      <AnimatePresence>
        {isOpen && suggestions.length > 0 && (
          <motion.div
            id="search-suggestions"
            role="listbox"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="absolute top-full left-0 right-0 mt-1.5 rounded-2xl border border-border bg-surface-elevated shadow-xl z-dropdown overflow-hidden py-1"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                onClick={() => handleSubmit(suggestion.text)}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors duration-100",
                  index === activeIndex
                    ? "bg-muted text-foreground"
                    : "text-text-muted hover:bg-muted hover:text-foreground"
                )}
              >
                <span className="flex-shrink-0">
                  {suggestionIcons[suggestion.type] || suggestionIcons.query}
                </span>
                <span className="flex-1 truncate">{suggestion.text}</span>
                {suggestion.type !== "query" && (
                  <span className="text-xs text-text-subtle capitalize">
                    {suggestion.type}
                  </span>
                )}
              </button>
            ))}

            {/* Full search option */}
            <div className="border-t border-border mt-1 pt-1 px-1">
              <button
                onClick={() => handleSubmit()}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-brand hover:bg-brand-muted transition-colors"
              >
                <IconSearch className="w-3.5 h-3.5" />
                Search for &quot;{query}&quot;
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
